(ns app.front.view.public-records.contractor-registrations
  "Public Records Contractor Registrations tab content"
  (:require [app.front.ui.sortable-table :as sortable-table]
            [app.front.view.public-records.common :as pr-common]
            [app.front.view.public-records.ui :as prui]
            [app.i18n.model :as i18n-model]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [re-frame.core-instrumented :as rf]))

;; ---- Data path interceptor --------------------------------------------------

(def data-path (rf/path ::data))

;; ---- Events -----------------------------------------------------------------

(rf/reg-event-fx ::ensure-init
  (fn [{:keys [db]} [_ branding]]
    (let [{current-branding :branding
           initialized?     :initialized?} (::data db)]
      (when (or (not initialized?)
                (not= current-branding branding))
        {:dispatch [::init branding]}))))

(rf/reg-event-fx ::init
  (fn [{:keys [db]} [_ branding]]
    {:db (assoc db ::data {:branding               branding
                           :initialized?           true
                           :visible-filters        nil
                           :selected-work-type-ids #{}
                           :sort-condition         (pr-common/default-sort-for-tab :public-records/contractor-registrations)
                           :results                []
                           :skip                   0
                           :limit                  20
                           :loading-more?          false
                           :total-count            0})
     :fx [[:dispatch [::search]]]}))

(rf/reg-event-fx ::search
  (fn [{:keys [db]} [_ {:keys [keep-results? sort-condition]}]]
    (let [data-db                                              (::data db)
          actual-sort                                          (or sort-condition (:sort-condition data-db))
          new-skip                                             (pr-common/search-skip (:skip data-db) keep-results?)
          updated-data-db                                      (cond-> (assoc data-db
                                                                              :skip new-skip
                                                                              :loading-more? (boolean keep-results?))
                                                                 actual-sort (assoc :sort-condition actual-sort))
          updated-db                                           (assoc db ::data updated-data-db)
          {:keys [branding selected-work-type-ids limit skip]} (::data updated-db)]
      {:db updated-db
       :fx [[:dispatch [:comm/command-monitored
                        {:command    :public-records/search-contractor-registrations
                         :method     :post
                         :data       (cond-> {:branding branding
                                              :skip     skip
                                              :limit    limit}
                                       (seq selected-work-type-ids) (assoc :work-type-ids selected-work-type-ids)
                                       actual-sort (assoc :sort-condition actual-sort))
                         :on-success [::search.success keep-results?]}]]]})))

(defn- visible-filter-configs [available-filters]
  [{:attribute          :work-types
    :visibility-options (:work-types available-filters)}])

(defn- search-filter-configs [work-type-ids work-type-labels selected-work-type-ids lang]
  [{:attribute    :work-types
    :id           "contractor-registration-work-type-filter"
    :options      work-type-ids
    :value        selected-work-type-ids
    :on-change    #(>evt [::set-selected-work-type-ids (set %)])
    :label-tr-key :public-records/trades
    :val->str     #(some-> work-type-labels
                           (get %)
                           (i18n-model/get-text-from-entities-or-map lang))}])

(defn- prune-selected-work-type-ids [selected-work-type-ids available-filters]
  (let [available-work-type-ids (into #{} (keep :db/id) (:work-types available-filters))]
    (set (filter available-work-type-ids selected-work-type-ids))))

(defn- fallback-work-type-texts
  "Fallback for hits where per-trade :work-types cannot be derived from optional ES :work-type-ids"
  [work-type-names lang]
  (let [base-lang      (i18n-model/get-base-lang lang)
        matching-names (or (seq (filter #(= lang (:entity/language %)) work-type-names))
                           (seq (filter #(= base-lang (:entity/language %)) work-type-names))
                           (seq (filter #(= base-lang (i18n-model/get-base-lang (:entity/language %)))
                                        work-type-names))
                           work-type-names)]
    (some->> matching-names
             (mapv :localized-string/text)
             not-empty)))

(defn- work-type-text-items [work-types lang]
  (when (seq work-types)
    (let [items (->> work-types
                     (keep (fn [{:db/keys           [id]
                                 :group.config/keys [names]}]
                             (some->> (i18n-model/get-text-from-entities-or-map names lang)
                                      (hash-map :key id :text))))
                     vec)]
      (when (= (count items) (count work-types))
        (not-empty items)))))

(defn work-type-texts [{:keys [work-types work-type-names]} lang]
  (or (some->> (work-type-text-items work-types lang)
               (mapv :text)
               not-empty)
      (fallback-work-type-texts work-type-names lang)))

(defn expandable-contractor-registration? [{:keys [work-type-count]}]
  (< 1 work-type-count))

(defn- single-work-type-text [{:keys [work-types work-type-names]} lang]
  (or (some-> (work-type-text-items work-types lang) first :text)
      (i18n-model/get-text-from-entities-or-map work-type-names lang)))

(defn- expanded-row-content [tr lang {:keys [work-types] :as result}]
  (when-some [trade-items (or (work-type-text-items work-types lang)
                              (some->> (work-type-texts result lang)
                                       (map-indexed (fn [idx trade-text]
                                                      {:key  (str "work-type-" idx)
                                                       :text trade-text}))
                                       not-empty))]
    [:div.p-4
     [prui/form-row
      (tr [:public-records/trades])
      [:ul.list-disc.ml-4.space-y-1
       (for [{:keys [key text]} trade-items]
         ^{:key key}
         [:li text])]]]))

(rf/reg-event-fx ::search.success
  data-path
  (fn [{:keys [db]} [_ keep-results? _status {:keys [total-count results available-filters]}]]
    (let [selected-work-type-ids (:selected-work-type-ids db)
          pruned-work-type-ids   (prune-selected-work-type-ids selected-work-type-ids
                                                               available-filters)
          visible-filters        (pr-common/initial-visible-filters (:visible-filters db)
                                                                    (visible-filter-configs available-filters)
                                                                    {:work-types pruned-work-type-ids})
          selections-changed?    (not= selected-work-type-ids pruned-work-type-ids)]
      {:db (assoc db
                  :total-count total-count
                  :available-filters available-filters
                  :visible-filters visible-filters
                  :selected-work-type-ids pruned-work-type-ids
                  :results (pr-common/merge-search-results (:results db)
                                                           results
                                                           keep-results?))
       :fx (cond-> []
             selections-changed?
             (conj [:dispatch [::search {:keep-results? false}]]))})))

(rf/reg-event-fx ::set-selected-work-type-ids
  data-path
  (fn [{:keys [db]} [_ work-type-ids]]
    {:db (assoc db
                :selected-work-type-ids work-type-ids
                :skip                   0
                :results                [])
     :fx [[:dispatch [::search]]]}))

(rf/reg-event-fx ::load-more
  data-path
  (pr-common/make-load-more-handler {:search-evt ::search}))

;; ---- Subscriptions ----------------------------------------------------------

(rf/reg-sub ::data :-> ::data)

(rf/reg-sub ::available-filters
  :<- [::data]
  :-> :available-filters)

(rf/reg-sub ::work-type-options
  :<- [::available-filters]
  :<- [:language]
  (fn [[available-filters lang]]
    (some->> (:work-types available-filters)
             not-empty
             (sort-by #(i18n-model/get-text-from-entities-or-map (:group.config/names %) lang))
             vec)))

(rf/reg-sub ::visible-filters
  :<- [::data]
  (fn [data _]
    (or (:visible-filters data) #{})))

(rf/reg-sub ::work-type-ids
  :<- [::work-type-options]
  (fn [work-type-options]
    (some->> work-type-options
             (map :db/id)
             vec
             not-empty)))

(rf/reg-sub ::work-type-labels
  :<- [::work-type-options]
  (fn [work-type-options]
    (some->> work-type-options
             (map (juxt :db/id :group.config/names))
             (into {}))))

(rf/reg-sub ::selected-work-type-ids
  :<- [::data]
  :-> :selected-work-type-ids)

(rf/reg-sub ::loading-more?
  :<- [::data]
  :-> :loading-more?)

(rf/reg-sub ::results
  :<- [::data]
  :-> :results)

(rf/reg-sub ::total-count
  :<- [::data]
  :-> :total-count)

(rf/reg-sub ::sort-condition
  :<- [::data]
  :-> :sort-condition)

(rf/reg-sub ::columns
  :<- [:tr]
  :<- [:language]
  :<- [::date-util/date-fn]
  (fn [[tr lang format-date] _]
    [[(tr [:public-records/registrant])
      (fn [result]
        (i18n-model/get-text-from-entities-or-map (:registrant-names result) lang))]
     [(tr [:public-records/trades])
      (fn [{:keys [work-type-count] :as result}]
        (if (= 1 work-type-count)
          (single-work-type-text result lang)
          (tr [:search/multiple-work-types])))]
     [(tr [:licence/valid-from])
      (fn [result]
        (some-> (:contractor-registration/valid-from result) format-date))
      {:sorting            (fn [a b]
                             (compare (:contractor-registration/valid-from a)
                                      (:contractor-registration/valid-from b)))
       :sort-attribute-key :contractor-registration/valid-from
       :on-sort-hook       (partial pr-common/on-sort-hook ::search :contractor-registration/valid-from)}]
     [(tr [:licence/valid-to])
      (fn [result]
        (some-> (:contractor-registration/valid-to result) format-date))
      {:sorting            (fn [a b]
                             (compare (:contractor-registration/valid-to a)
                                      (:contractor-registration/valid-to b)))
       :sort-attribute-key :contractor-registration/valid-to
       :on-sort-hook       (partial pr-common/on-sort-hook ::search :contractor-registration/valid-to)}]
     [(tr [:public-records/state])
      (fn [result]
        (tr [(:domain/state result)]))]]))

(rf/reg-sub ::initial-sorting
  :<- [::sort-condition]
  :<- [::columns]
  (fn [[sort-condition columns] _]
    (pr-common/sort-condition->initial-sorting sort-condition columns)))

;; ---- Views ------------------------------------------------------------------

(defn- search-filter [tr]
  (let [work-type-ids          (<sub [::work-type-ids])
        visible-filters        (<sub [::visible-filters])
        work-type-labels       (<sub [::work-type-labels])
        selected-work-type-ids (<sub [::selected-work-type-ids])
        lang                   (<sub [:language])]
    [pr-common/render-search-filters tr
     visible-filters
     (search-filter-configs work-type-ids work-type-labels selected-work-type-ids lang)]))

(defn- search-result-table [tr]
  (let [lang (<sub [:language])]
    [sortable-table/table
     {:id              "contractor-registration-search-results"
      :cols            (<sub [::columns])
      :configurable?   true
      :id-func         :id
      :initial-sorting (<sub [::initial-sorting])
      :items           (<sub [::results])
      :tr              tr
      :sticky-header?  true
      :expanded-func   #(when (expandable-contractor-registration? %)
                          (expanded-row-content tr lang %))}]))

(defn tab-view []
  (let [tr              (<sub [:tr])
        visible-filters (<sub [::visible-filters])
        total-count     (<sub [::total-count])
        in-progress?    (<sub [:comm/command-processing? :public-records/search-contractor-registrations])]
    [:div
     [search-filter tr]
     [:div {:class (if (seq visible-filters) "mt-8 mb-4" "mb-4")}
      [prui/results-count tr total-count in-progress?]]
     [pr-common/render-paginated-table
      {:tr            tr
       :in-progress?  in-progress?
       :loading-more? (<sub [::loading-more?])
       :results       (<sub [::results])
       :total-count   total-count
       :table         [search-result-table tr]
       :load-more-evt ::load-more}]]))
